import { useEffect, useMemo, useRef, useState } from 'react'

import { instantOf } from '../freshness.js'
import BoxScore from './BoxScore.jsx'
import Flow from './Flow.jsx'
import { GameFloor } from './Floor.jsx'
import Hero from './Hero.jsx'
import Plays from './Plays.jsx'
import ShotChart from './ShotChart.jsx'
import { FantasyChips } from './Fantasy.jsx'
import { chartStyle, teamFor, toneStyle } from './colors.js'
import { chipsFor, mineIds, scoringStats } from './fantasyModel.js'
import { activeSection, gameFloor, gameFun, heroStrip, heroStripAge, liveCallout, sectionsFor } from './funModel.js'
import { heroModel, tiles } from './gameModel.js'
import { BOX_WIDE_PX, GAME_WIDE_PX, HERO_WIDE_PX, SURNAME_ONLY_BELOW_PX, useBelowWidth, useMinWidth } from './layout.js'
import { useHoopsGame } from './poll.js'
import { isPlainKey, panelId, tabId, tabTarget } from './tabs.js'

// One game: the hero, the five on the floor, the four tiles, and the box score, plays, shot chart and flow. P6-328b
// (GN-1b), and P6-330b (GN-3b) for the fun layer.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT DRAWS WHAT IT HAS, AND SAYS NOTHING ABOUT WHAT IT DOES NOT
// ═══════════════════════════════════════════════════════════════════════════
//
// The game's own object (`3a/hoops/games/<id>.json`) can be unread — the first request has not
// answered, or was refused — while the board's card for the same game is in hand. Then the hero is
// drawn from the CARD (it has the teams, the score, the stage of the game and its age) and the tiles,
// the box score and the plays, which only the game's object has, are simply not drawn. There is no
// error sentence and no skeleton: an unreadable feed is the header and what the page last knew
// (HP5, and the presence handoff's unreachable rule). With neither, the page is the way back to
// Tonight and nothing else.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SECTIONS ARE THE DATA THE GAME HAS, AND THE LAYOUT HAS THREE SHAPES
// ═══════════════════════════════════════════════════════════════════════════
//
// The sections are `funModel.js::sectionsFor`'s: Box score and Plays always, a Shot chart when the game has a shot with
// a position, and Flow when it has a curve to draw or a callout to list. A game frozen before the producer published
// shots, or a preseason game with no curve, simply has fewer: no empty segment and no control that opens nothing.
//
//   below 1000px   one column, and a segment control that picks one section
//   from 1000px    the box score stays open on the left and the other sections sit beside it, as segments of their
//                  own (Plays, and Shot chart and Flow where there are any). With nothing to choose between (a game
//                  that has only its plays) there is NO control: a one-option tab strip is a dead control.
//
// The four tiles are not in a segment: in the design they sit under "Flow", and GN-1b keeps them where every game has
// them, always drawn, between the hero and the sections (a game with no curve and no callouts has no Flow, and still
// has its tiles, which come straight from the feed).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE FIVE ON THE FLOOR IS FOR A GAME BEING PLAYED, AT EVERY WIDTH
// ═══════════════════════════════════════════════════════════════════════════
//
// Under the hero, from the box score's `oncourt`. The design's "always" (HANDOFF flag 1) is about width: a card shows
// the faces from 720px and the game view shows them everywhere. It is NOT about status: a final game's blob still flags
// the last five a side (the recorded final has five each), and ten players "on the floor" of a finished game is a claim
// nobody made, so `funModel.js::gameFloor` answers `null` for anything but a live game.

function Tiles({ game }) {
  return (
    <section className="d2d-hoops__tiles" aria-label="Swings" data-testid="hoops-tiles">
      <h3 className="d2d-hoops__sectiontitle d2d-micro">Swings</h3>
      <div className="d2d-hoops__tilegrid">
        {tiles(game).map((tile) => (
          <div className="d2d-hoops__tile" key={tile.key} data-testid={`hoops-tile-${tile.key}`}>
            <div className="d2d-hoops__tilelabel d2d-micro">{tile.label}</div>
            <div className="d2d-hoops__tilevalue">{tile.value}</div>
            {tile.sub === null ? null : <div className="d2d-hoops__tilesub">{tile.sub}</div>}
          </div>
        ))}
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SECTION TABS ARE THE WAI-ARIA TABS PATTERN (P6-334, GN-7; GN-3b's concern 11)
// ═══════════════════════════════════════════════════════════════════════════
//
// A phone's segment control and a desktop's right-hand column are the same thing, a `tablist`, and until GN-7 it was a row
// of buttons a keyboard could Tab through one at a time. Now:
//
//   * Left and Right move to the neighbouring tab and WRAP at both ends; Home and End go to the first and the last. A
//     modifier key (Alt+Left is the browser's Back) is left to the browser;
//   * ONLY THE SELECTED TAB IS IN THE TAB ORDER (a roving `tabindex`: 0 on it, -1 on the rest), so Tab leaves the strip
//     for the section under it instead of walking four stops, and SELECTION FOLLOWS FOCUS: the key that moves focus also
//     opens the section (every section is already in memory, so there is nothing to wait for and no reason to make a
//     member press Enter on top of an arrow);
//   * each tab names its panel with `aria-controls` and the panel is labelled by its tab, so a screen reader says
//     "Shot chart, tab, 3 of 4" going in and "Shot chart, tab panel" coming out.
//
// ⚠️ EVERY TAB'S PANEL IS IN THE DOCUMENT, AND ONLY THE OPEN ONE HAS ANYTHING IN IT. A panel for each section is rendered
// with the `hidden` attribute on the ones that are not open and nothing inside them, so every `aria-controls` points at an
// element that exists (an id with no element behind it is a broken reference, not a shorter list), while the shot chart's
// 200 dots and the 50 plays are still built only when their section is open: opening the chart again draws it in again,
// as the design says, and the board's memory is the same as it was.
//
// `.d2d-hoops__segments`/`__segment` are GN-1b's and unchanged: this adds behaviour and attributes, no class and no rule.
// The arithmetic (which tab a key goes to, what the ids are) is `tabs.js`'s, and this is the wiring.

function SectionTabs({ sections, active, onChoose }) {
  const buttons = useRef(new Map())

  const onKeyDown = (event, index) => {
    if (!isPlainKey(event)) return
    const target = tabTarget(event.key, index, sections.length)
    if (target === null) return
    event.preventDefault()
    const { key } = sections[target]
    onChoose(key)
    buttons.current.get(key)?.focus()
  }

  return (
    <div className="d2d-hoops__segments" role="tablist" aria-label="Game sections">
      {sections.map(({ key, label }, index) => (
        <button
          type="button"
          role="tab"
          key={key}
          id={tabId(key)}
          ref={(node) => {
            if (node === null) buttons.current.delete(key)
            else buttons.current.set(key, node)
          }}
          className="d2d-hoops__segment"
          aria-selected={active === key ? 'true' : 'false'}
          aria-controls={panelId(key)}
          tabIndex={active === key ? 0 : -1}
          data-testid={`hoops-seg-${key}`}
          onClick={() => onChoose(key)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// What a keyboard can land on. A disabled control, a hidden input and an element taken out of the tab order (`tabindex="-1"`)
// are not stops, so none of them counts.
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

/**
 * One tab panel. P6-343: *"When the tab panel does not contain any focusable elements, the tabpanel should set
 * `tabindex="0"`"* (the WAI-ARIA tabs pattern, which GN-7 built the tabs to), so a keyboard user who arrows onto a tab and
 * presses Tab reaches the content under it instead of skipping past it. A panel with a button or a slider in it already has
 * a stop, and giving it a second would be a tab stop that does nothing.
 *
 * ⚠️ DECIDED FROM THE DOCUMENT, NOT FROM THE SECTION'S NAME. Whether a section holds a focusable thing depends on the
 * game (a phone's box score is buttons only for players who played, the shot chart has controls only when there are shots),
 * and the content changes with every poll, so the check runs after every render and sets the attribute on the node
 * itself (React does not manage it, so there is no render loop). A panel that is `hidden`, or empty, never gets one.
 */
function Panel({ id, labelledBy, open, children }) {
  const node = useRef(null)
  useEffect(() => {
    const element = node.current
    if (element === null) return
    const wants = open && element.querySelector(FOCUSABLE) === null
    if (wants) element.setAttribute('tabindex', '0')
    else element.removeAttribute('tabindex')
  })
  return (
    <div role="tabpanel" id={id} aria-labelledby={labelledBy} ref={node} hidden={!open}>
      {open ? children : null}
    </div>
  )
}

/** One panel a section, the open one holding the section and the rest empty and `hidden` (see the note above). */
function SectionPanels({ sections, active, children }) {
  return sections.map(({ key }) => (
    <Panel key={key} id={panelId(key)} labelledBy={tabId(key)} open={key === active}>
      {children}
    </Panel>
  ))
}

export default function GameView({ gameId, card, teams, nowMs, timeZone, onBack, fantasy = null }) {
  const { game } = useHoopsGame(gameId)
  const wideBox = useMinWidth(BOX_WIDE_PX)
  const wideHero = useMinWidth(HERO_WIDE_PX)
  const wideGame = useMinWidth(GAME_WIDE_PX)
  const surnameOnly = useBelowWidth(SURNAME_ONLY_BELOW_PX)
  // `null` is "the default for this width", so a width change or a section that is gone falls back to it.
  const [chosen, setChosen] = useState(null)
  const [shotSide, setShotSide] = useState('both')
  // The shots and the curve are the biggest things on the page and the page re-renders every second: they are worked
  // out once per object the poll hands over, not once per tick.
  const fun = useMemo(() => gameFun(game), [game])

  const hero = heroModel({ game, card, nowMs })
  const away = hero ? teamFor(teams, hero.away.tricode) : null
  const home = hero ? teamFor(teams, hero.home.tricode) : null
  // The board's table entry for each side, resolved once: the colours come from it (`toneStyle`) and so do
  // the logos (`Marks.jsx`), in the hero and over each team's box score.
  const teamOf = { away, home }

  const status = hero?.status
  const win = heroStrip({ game, card, status })
  const winAge = heroStripAge({ game, card, status }, nowMs)
  const callout = status === 'live' ? liveCallout(card) : null
  const floor = gameFloor(game, status)

  // P6-333a (GN-6a): the viewer's league, when they have one. This game's own object (polled here) is the freshest box
  // there is for it, so it replaces the panel's copy of the same game. The Fpts column reads `boxFantasy`.
  const boxFantasy = fantasy === null ? null : { stats: scoringStats(fantasy.blob), mine: mineIds(fantasy.blob) }
  const mine =
    fantasy === null || game === null
      ? []
      : chipsFor(fantasy.blob, new Map([...fantasy.boxes, [gameId, game]]), card === null ? [] : [card], gameId)

  const sections = sectionsFor({ hasShots: fun.hasShots, hasFlow: fun.hasFlow, wide: wideGame })
  const active = activeSection(sections, chosen, wideGame)
  const tabs = sections.length > 1

  const panel = (key) => {
    if (key === 'box') return <BoxScore game={game} teamOf={teamOf} wide={wideBox} narrow={surnameOnly} fantasy={boxFantasy} />
    if (key === 'plays') return <Plays game={game} source={hero.source} stampMs={hero.stampMs} nowMs={nowMs} />
    if (key === 'shots') {
      return (
        <ShotChart
          chart={fun.chart}
          side={shotSide}
          onSide={setShotSide}
          away={hero.away.tricode}
          home={hero.home.tricode}
          live={status === 'live'}
        />
      )
    }
    return (
      <Flow
        curve={fun.curve}
        swings={fun.swings}
        away={hero.away.tricode}
        home={hero.home.tricode}
        source={game.win_probability_source}
        stampMs={instantOf(game.win_probability_as_of)}
        nowMs={nowMs}
      />
    )
  }

  return (
    <div
      className="d2d-hoops__game"
      data-testid="hoops-game"
      data-game-id={gameId}
      style={{ ...toneStyle(away, home), ...chartStyle(away, home) }}
    >
      <div>
        <button type="button" className="d2d-hoops__back" data-testid="hoops-back" onClick={onBack}>
          ← Tonight
        </button>
      </div>

      {hero === null ? null : (
        <Hero
          hero={hero}
          game={game}
          teamOf={teamOf}
          wide={wideHero}
          nowMs={nowMs}
          timeZone={timeZone}
          win={win}
          winAge={winAge}
          callout={callout}
        />
      )}

      {game === null || hero === null ? null : (
        <>
          {floor === null ? null : <GameFloor floor={floor} sides={{ away: hero.away, home: hero.home }} />}
          <FantasyChips chips={mine} testId="hoops-game-mine" />
          <Tiles game={game} />
          {wideGame ? (
            <div className="d2d-hoops__gamegrid">
              <BoxScore game={game} teamOf={teamOf} wide={wideBox} narrow={surnameOnly} fantasy={boxFantasy} />
              <div className="d2d-hoops__gamecol">
                {tabs ? (
                  <>
                    <SectionTabs sections={sections} active={active} onChoose={setChosen} />
                    <SectionPanels sections={sections} active={active}>
                      {panel(active)}
                    </SectionPanels>
                  </>
                ) : (
                  // One section and no control: a panel with no tab to belong to is not a tab panel.
                  <div>{panel(active)}</div>
                )}
              </div>
            </div>
          ) : (
            <>
              <SectionTabs sections={sections} active={active} onChoose={setChosen} />
              <SectionPanels sections={sections} active={active}>
                {panel(active)}
              </SectionPanels>
            </>
          )}
        </>
      )}
    </div>
  )
}
